import { observable, reaction } from "mobx";
import { SaveStatuses } from "./save-statuses";
import { axios } from "../../../../utils/axios";

class QuizObjectPatchable {
    @observable deleted: boolean = false;
    @observable status: SaveStatuses = SaveStatuses.UNCHANGED;
    @observable statusMessage: string = "";
    public quizObjectId: string;
    public quizObjectStatus: Map<string, any>;

    _id: string | undefined;

    constructor(quizObjectId: string, quizObjectStatus: Map<string, any>) {
        this.quizObjectId = quizObjectId;
        this.quizObjectStatus = quizObjectStatus;
    }

    patchURL() {
        return `/quiz_objects/${this.quizObjectId}`;
    }
    /**
     * This will tell you if there is already a save/restore/delete in progress.
     */
    busy(): boolean {
        return (
            this.status == SaveStatuses.SAVING ||
            this.status == SaveStatuses.RESTORING ||
            this.status == SaveStatuses.DELETING
        );
    }

    /**
     * This function sends a patch message to the backend to delete the card table.
     */
    async _patchDelete(patchBody: any) {
        if (this.busy()) return;
        this.status = SaveStatuses.DELETING;
        return axios()
            .patch(this.patchURL(), patchBody)
            .then(() => {
                this.deleted = true;
                this.status = SaveStatuses.DELETED;
            })
            .catch((res: any) => {
                console.log(res);
                this.status = SaveStatuses.ERROR;
            });
    }

    /**
     * _patchRestore will restore a deleted quiz object element.  The difference between this and
     * patchCreate is mostly in which status the process passes through.  This doesn't actually
     * restore the original object, it creates a new object with a different ID.  This is problematic
     * since it will affect anything that relates to it.  If we decide to implement comments in the
     * future, it won't work.  Need to change how this works on the backend.
     * @param patchBody
     */
    async _patchRestore(patchBody: any) {
        if (this.busy()) return;
        this.status = SaveStatuses.RESTORING;
        return axios()
            .patch(this.patchURL(), patchBody)
            .then((res: any) => res.data)
            .then((res: any) => {
                this._id = res.updated._id;
                this.status = SaveStatuses.RESTORED;
                this.deleted = false;
            })
            .catch((res: any) => {
                this.status = SaveStatuses.ERROR;
                this.statusMessage = res;
            });
    }

    /**
     * _patchPersist persists the data in the quiz object element.  This usually
     * is triggered by a mobx-reaction.
     * @param patchBody
     */
    async _patchPersist(patchBody: any, objectType: string, cb: Function) {
        if (this.busy() || this.deleted) return;
        this.status = SaveStatuses.SAVING;

        this.quizObjectStatus.set(objectType, SaveStatuses.SAVING)
        return axios()
            .patch(`/quiz_objects/${this.quizObjectId}`, patchBody)
            .then((res: any) => res.data)
            .then((res: any) => {
                this._id = res.updated._id;
                this.status = SaveStatuses.SAVED;
                this.quizObjectStatus.set(objectType, SaveStatuses.SAVED)
                cb(res);
            })
            .catch((err: any) => {
                this.quizObjectStatus.set(objectType, SaveStatuses.ERROR)
                this.status = SaveStatuses.ERROR;
            });
    }
}

export default QuizObjectPatchable;
